import { BoostAdFlagType } from '@zg-rentals/ts-rental-manager-api-client';
import { Box, Icon, PropertyCard, Text, TextButton } from '@zillow/constellation-10';
import { IconExternalFilled } from '@zillow/constellation-icons';
// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
import { styled } from '@/styled-system/jsx';
import { getDefaultRentStartingAt } from './BoostWidget';

export interface ZillowAdPreviewProps {
  adFlag: BoostAdFlagType;
  customFlag: string;
  imageSrc: string;
  title: string;
  subheader: string;
  rentStartingAtPrice: number;
  websiteLink: string;
  displayRentStartingAt: boolean;
  customDetails: string;
}

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
const StyledPropertyCard = styled(PropertyCard, {
  base: {
    minWidth: '100% !important',
    height: 'min-content !important',
    boxShadow: 'none',
    border: '1px solid',
    borderColor: 'border.muted',
    borderRadius: '4px',
    '& a': {
      // oxlint-disable-next-line constellation/prefer-semantic-tokens -- no semantic token resolves to blue.400 (#65b4ff) in the zillow light block; text.action.hero.default is #0041d9 there and only matches in dark mode
      color: 'blue.400',
      marginTop: 0,
    },
  },
});

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
const CustomBadge = styled(PropertyCard.Badge, {
  base: {
    height: 'min-content',
    textTransform: 'capitalize',
  },
  variants: {
    addMargin: {
      true: {
        marginTop: 'sm',
        marginRight: 'sm',
      },
    },
  },
});

export const ZillowAdPreview = ({
  imageSrc,
  adFlag,
  customFlag,
  rentStartingAtPrice,
  subheader,
  title,
  websiteLink,
  displayRentStartingAt,
  customDetails,
}: ZillowAdPreviewProps) => {
  return (
    <>
      <StyledPropertyCard
        aria-label={title}
        badge={
          adFlag !== BoostAdFlagType.NONE && (
            <CustomBadge css={{ marginTop: '150' }}>
              {adFlag === BoostAdFlagType.CUSTOM
                ? customFlag || '1 month free'
                : adFlag.replace(/_/g, ' ').toLowerCase()}
            </CustomBadge>
          )
        }
        data={{
          dataArea1: title,
          dataArea2: subheader ? (
            <PropertyCard.HomeDetails data={subheader.split(',').map((val) => ({ value: '', label: val.trim() }))} />
          ) : undefined,
          dataArea3: displayRentStartingAt ? getDefaultRentStartingAt(rentStartingAtPrice) : (customDetails ?? ''),
          dataArea5: websiteLink ? (
            <Box>
              <TextButton asChild>
                <a href={websiteLink} target="_blank" rel="noopener noreferrer">
                  <Box css={{ display: 'flex', alignItems: 'center', gap: 'xs' }}>
                    <Text
                      textStyle="body-sm-bold"
                      css={{
                        color:
                          'blue.400' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ambiguous semantic-token suggestion, not migrating in a lint opt-in */,
                      }}
                    >
                      Visit website
                    </Text>
                    <Icon
                      css={{
                        color:
                          'blue.400' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ambiguous semantic-token suggestion, not migrating in a lint opt-in */,
                      }}
                    >
                      <IconExternalFilled />
                    </Icon>
                  </Box>
                </a>
              </TextButton>
            </Box>
          ) : undefined,
        }}
        photoBody={<PropertyCard.Photo src={imageSrc} alt={title} />}
        saveButton={<CustomBadge addMargin>Sponsored</CustomBadge>}
        elevated={false}
      />
    </>
  );
};
